<template>
  <pie-chart-card
    class="parent-type-card"
    :loading="loading"
    :chart-data="totalValue === 0 ? [] : data"
    clickable
    @click="handler($event)"
  >
    <template #header>
      <dashboard-card-header
        :title="$t('goalInsightsHealth.parentType.cardTitle')"
        text-key="goalInsightsHealth.parentType.cardHelp"
        :can-edit="canEdit"
        :link="settingsLink"
        :loading="loading"
      />
    </template>
  </pie-chart-card>
</template>

<script>
import DashboardCardHeader from '@/components/goal-insights/health/DashboardCardHeader.vue';
import PieChartCard from '@/components/dashboard/PieChartCard.vue';
import useAccess from '@/composables/access/access';
import useDebounce from '@/composables/debounce';
import useGoalTypeProperty from '@/composables/customize-page/goal-type-property';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePublishedAtFilter from '@/composables/goal/published-at-filter';
import useSubscription from '@/composables/subscription/subscription';
import { AND } from 'shared/api/query/filter';
import { DIRECT_PROPERTY_PARENT_KEY } from '@/lib/constants';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { accessGroupFlag, routeName } from 'shared/constants.json';
import { applyBaseFilter } from '@/lib/filter/base-filter';
import { dogma } from '@/api';
import { goalTypesFilter } from '@/lib/filter/goal/goals-type-filter';
import { parentTypeFilter } from '@/lib/filter/goal/parent-scope-filter';
import { queryParentType } from '@/api/query/goal-insights-health';
import { textByLang } from 'shared/lib/language';

export default {
  name: 'ParentTypeCard',
  props: {
    selectedCycles: {
      type: Array,
      required: true,
    },
    viewFilter: {
      type: Object,
      required: true,
    },
    goalType: {
      type: Object,
      required: true,
    },
  },
  emits: ['show-goal-list', 'show'],
  setup() {
    const { userLang } = useLoggedInUser();
    const { debounce } = useDebounce();
    const { subscribe } = useSubscription();

    const { goalTypeProperty } = useGoalTypeProperty();
    const { publishedAtFilter } = usePublishedAtFilter();
    const { userHasRight } = useAccess();
    return {
      debounce,
      subscribe,

      userLang,
      goalTypeProperty,
      publishedAtFilter,
      userHasRight,
    };
  },
  components: { DashboardCardHeader, PieChartCard },
  data() {
    return {
      loading: false,
      data: [
        { key: 'right', label: this.$t('goalInsightsHealth.parentType.labelRight'), value: 0, series: [], color: 'green' },
        { key: 'wrong', label: this.$t('goalInsightsHealth.parentType.labelWrong'), value: 0, series: [], color: 'red' },
      ],
    };
  },
  computed: {
    series() {
      return this.data.map((e) => e.series).flat().map((e) => e.uid);
    },
    totalValue() {
      return this.data.reduce((acc, e) => acc + e.value, 0);
    },
    shouldShow() {
      return this.goalType.parentRequired === true // parent is required
          || this.goalType.canBeChildOf.length !== this.goalTypeProperty.options.length; // parent types are not the full set
    },
    canEdit() {
      return this.userHasRight([accessGroupFlag.goalCycleWriteAccess]);
    },
    settingsLink() {
      return { name: routeName.goalTypes, params: { optionId: this.goalType.uid }, query: { propKey: DIRECT_PROPERTY_PARENT_KEY } };
    },
    gqlFilterObject() {
      const handler = new GoalFilterHandler();
      return handler.Translate(this.viewFilter);
    },
    gqlFilter() {
      return this.gqlFilterObject.filterTree;
    },
  },
  methods: {
    handler(bucket) {
      const bucketFilter = [];
      switch (bucket.key) {
        case 'right':
          bucketFilter.push(parentTypeFilter(this.goalTypeProperty, this.goalType));
          break;
        case 'wrong':
          bucketFilter.push(parentTypeFilter(this.goalTypeProperty, this.goalType, true));
          break;
        default:
          break;
      }

      this.$emit('show-goal-list', {
        filter: {
          op: AND,
          children: [
            this.viewFilter,
            goalTypesFilter(this.goalTypeProperty, this.goalType),
            ...bucketFilter,
          ],
        },
        title: this.$t('goalInsightsHealth.parentType.dialogTitle', { label: bucket.label, goalType: textByLang(this.goalType.label, this.userLang) }),
      });
    },
    retrieveParentTypeRestriction() {
      if (!this.shouldShow) {
        this.$emit('show', false);
        return;
      }
      this.$emit('show', true);

      this.loading = true;
      dogma.query(applyBaseFilter({
        queries: [
          ...queryParentType({
            goalCycles: this.selectedCycles,
            filter: this.gqlFilter,
            varBlocks: this.gqlFilterObject.varBlocks,
            goalTypeOption: this.goalType,
          }),
        ],
        operator: AND,
        baseFilter: this.publishedAtFilter,
      })).then((response) => {
        this.loading = false;
        if (response.status !== 200) {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
          return;
        }

        this.data.forEach((e) => {
          switch (e.key) {
            case 'right':
            case 'wrong':
            case 'noParent':
              e.series = response.data[e.key];
              e.value = e.series.length;
              break;
            default:
              break;
          }
        });
      });
    },
    fetch(wait) {
      this.debounce(this.retrieveParentTypeRestriction, wait, 'retrieveParentTypeRestriction');
    },
  },
  watch: {
    selectedCycles(val, oldVal) {
      if (JSON.stringify(val) === JSON.stringify(oldVal)) {
        return;
      }
      this.fetch(1000);
    },
    gqlFilterObject: {
      handler(val, oldVal) {
        if (JSON.stringify(val) === JSON.stringify(oldVal)) {
          return;
        }
        this.fetch(1000);
      },
      deep: true,
    },
  },
  created() {
    this.fetch(0);

    const fn = this.fetch;
    this.subscribe({
      queryFn: Promise.resolve(),
      syncFn({ isSelfEvent }) {
        const delay = isSelfEvent ? 1000 : 30000;
        fn(delay);
      },
      model: 'goal',
      includeSelfEvents: true,
    });
  },
};
</script>
